const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/old_components/components/StaticText.tsx';
import _ from 'lodash';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import { Typography, TypographyProps } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { FormElementProps } from '../defs';
import { ElementFrame, ElementFrameProps } from './private/ElementFrame';
import { css } from '@emotion/css';
import { ReactNode, useMemo } from 'react';

interface Props extends FormElementProps {
    text?: string | MessageDescriptor | undefined | null;
    component?: ElementFrameProps['component'];
    textComponent?: 'div' | 'span' | 'label' | 'h2';
    isLink?: boolean;
    isBold?: boolean;
    isSmall?: boolean;
    isBig?: boolean;
    allowMarkDown?: boolean;
    lineHeight?: number;
    color?: TypographyProps['color'];
    variant?: TypographyProps['variant'];
    noWrap?: TypographyProps['noWrap'];
    gutterBottom?: TypographyProps['gutterBottom'];
    onClick?: (evt?: any) => void;
    isFormView?: boolean;
    isMandatory?: boolean;
    formatMessageParams?: any;
}

const paddingLeftStyle = css`
    padding-left: 2px;
`;

function useCSSClasses(props: Props) {
    const theme = useTheme();
    return useMemo(
        () => ({
            root: css`
                cursor: ${props.isLink ? 'pointer' : 'auto'};
                line-height: ${props.lineHeight};
                font-weight: ${props.isBold ? 'bold' : 'inherit'};
                font-size: ${props.isSmall
                    ? theme.typography.body2.fontSize
                    : props.isBig
                      ? theme.typography.h6.fontSize
                      : undefined};
                text-align: left;

                &:hover {
                    color: ${props.isLink ? theme.palette.text.primary : 'inherit'};
                }
            `,
        }),
        [theme, props.isLink, props.lineHeight, props.isBold, props.isSmall, props.isBig]
    );
}

// Removed ctx-based field with commit 7da5929cc
export const StaticText = (props: Props) => {
    const { formatMessage } = useIntl();
    const classes = useCSSClasses(props);
    // only pass keys which really do exists, otherwise ARIA/VoiceOver talks chaos
    const typographyProps: any = {
        component: props.textComponent ?? 'span',
        color: props.color ?? (props.isLink ? 'primary' : undefined),
        variant: props.variant,
        noWrap: props.noWrap,
        gutterBottom: props.gutterBottom,
        classes: classes,
    };
    if (props.textComponent === 'label' && props.forId) {
        typographyProps.htmlFor = props.forId;
    }
    const mandaStar = (
        // aria-hidden prevents speaking out "star" for the *.
        <span aria-hidden={true} className={paddingLeftStyle}>
            *
        </span>
    );
    return _.flow([
        () => {
            if (!props.text) {
                return '';
            } else if (typeof props.text === 'string') {
                return props.text;
            } else {
                // We assume it's a MessageDescriptor.
                return formatMessage(props.text, props.formatMessageParams);
            }
        },
        (text: string) => {
            if (!props.allowMarkDown) {
                if (props.isMandatory && !props.isFormView) {
                    return (
                        <>
                            {text}
                            {mandaStar}
                        </>
                    );
                } else {
                    return <>{text}</>;
                }
            } else {
                if (props.isMandatory && !props.isFormView) {
                    return (
                        <>
                            <ReactMarkdown>{text}</ReactMarkdown>
                            {mandaStar}
                        </>
                    );
                } else {
                    return <ReactMarkdown>{text}</ReactMarkdown>;
                }
            }
        },
        (wrappedText: ReactNode) => (
            <ElementFrame
                className={props.className}
                style={props.style}
                component={props.component}
                formEditClassName={props.formEditClassName}
                formEditStyle={props.formEditStyle}
                formViewClassName={props.formViewClassName}
                formViewStyle={props.formViewStyle}
                onClick={props.onClick}
                forId={props.forId}
                isFormView={props.isFormView}
            >
                <Typography {...typographyProps}>{wrappedText}</Typography>
            </ElementFrame>
        ),
    ])();
};
